/**
 * useTutorTipsConfig — React Query hook for GET /system-config/tutor-tips.
 * Implements US-001 / T-hook / API-001.
 *
 * Pattern evidence:
 *   - useTopupPacks.ts L50–55 (useQuery + staleTime: 5 * 60 * 1000)
 *   - useStudentConfig.ts L24–39 (graceful fallback to default on error/absent)
 *
 * Graceful fallback (DD-002 three-layer posture): if the endpoint fails or returns an
 * out-of-range value, we use DEFAULT_TIP_COUNTDOWN_SECONDS rather than blocking the session.
 */

import { useQuery } from '@tanstack/react-query';
import { getTutorTipsConfig } from '@/services/tutorTips';
import { DEFAULT_TIP_COUNTDOWN_SECONDS } from '@/types/tutorTips';
import type { TutorTipsConfig } from '@/types/tutorTips';

export interface UseTutorTipsConfigReturn {
  /** Resolved, clamped countdown seconds. Falls back to DEFAULT on error/out-of-range (API-001). */
  countdownSeconds: number;
  /** True during initial config fetch. Parent may render the popup with the default value in the meantime. */
  isLoading: boolean;
}

export function useTutorTipsConfig(): UseTutorTipsConfigReturn {
  const { data, isLoading } = useQuery<TutorTipsConfig, unknown>({
    queryKey: ['system-config', 'tutor-tips'],
    queryFn: () => getTutorTipsConfig(),
    staleTime: 5 * 60 * 1000, // 5 min — matches backend SystemConfigService cache TTL (NFR2)
  });

  // Graceful fallback (API-001): clamp to [1, 30]; any out-of-range or absent value
  // uses the constant so the session is never blocked by a bad config response.
  const raw = data?.tipPopupCountdownSeconds;
  const countdownSeconds =
    typeof raw === 'number' && raw >= 1 && raw <= 30
      ? Math.round(raw)
      : DEFAULT_TIP_COUNTDOWN_SECONDS;

  return { countdownSeconds, isLoading };
}
